import React from 'react';
import { BrowserRouter } from 'react-router-dom';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import * as Sentry from '@sentry/react';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v6';

import ApolloGQLProvider from 'app/graphql/client';
import { store } from 'app/store';
import MainRoute from 'routes';

// globals.css holds the Tailwind layers + design tokens. Imported as a
// plain CSS side-effect so PostCSS owns it directly rather than going
// through Sass (which prints a deprecation warning every reload).
import './styles/globals.css';
import './App.scss';
import { env } from 'config/env';
import { FlagsProvider } from 'providers/FlagsProvider';
import { ThemeProvider } from 'providers/ThemeProvider';
import { TooltipProvider } from 'components/ui/tooltip';
import { Toaster } from 'components/ui/sonner';
import { SyncUserWithSentry } from 'components/SyncUserWithSentry';
import { AuthProvider } from 'providers/AuthProvider';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

const queryClient = new QueryClient();

const container = document.getElementById('root');
const root = container && createRoot(container);

Sentry.init({
  dsn: 'https://fdf46288a90649c1b8dfdb93192b75cd@o4504131870916608.ingest.sentry.io/4504234997710848',
  enabled: env.VITE_NODE_ENV !== 'development',
  integrations: [Sentry.browserTracingIntegration()],

  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 1.0,
  environment: env.VITE_NODE_ENV,
});

if (!root) {
  throw new Error('Root element (id=root) not found; console is broken and will not render.');
}

root.render(
  <React.StrictMode>
    <BrowserRouter>
      <NuqsAdapter>
        <QueryClientProvider client={queryClient}>
          <ApolloGQLProvider>
            <Provider store={store}>
              <SyncUserWithSentry />
              <ThemeProvider>
                <AuthProvider>
                  <FlagsProvider>
                    <TooltipProvider>
                      <MainRoute />
                    </TooltipProvider>
                    <Toaster />
                  </FlagsProvider>
                </AuthProvider>
              </ThemeProvider>
            </Provider>
          </ApolloGQLProvider>
        </QueryClientProvider>
      </NuqsAdapter>
    </BrowserRouter>
  </React.StrictMode>,
);
